import React from "react";
import {
    alpha,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import InfoRounded from "@mui/icons-material/InfoRounded";
import Button from "../Button";
import { FONTS } from "../../constants/styles";

export interface AlertModalProps {
    open: boolean;
    onClose: () => void;
    alertText: React.ReactNode; // both string and JSX
    headingText?: string;
    buttonText?: string;
    onAction?: () => void;
    icon?: React.ReactNode;
}

const AlertModal: React.FC<AlertModalProps> = ({
    open,
    onClose,
    alertText,
    headingText = "Attention",
    buttonText = "Later",
    onAction,
    icon,
}) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    p: 3,
                    textAlign: "center",
                    boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
                    maxWidth: 360,
                    mx: 2,
                },
            }}
            className="force-dynamic-translation"
        >
            <DialogTitle sx={{ p: 0, pb: 0.5 }}>
                <Stack spacing={2} alignItems="center">
                    {/* Tinted icon box */}
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "14px",
                            bgcolor: alpha(hex, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            color: hex,
                        }}
                    >
                        {icon ?? <InfoRounded sx={{ fontSize: 24 }} />}
                    </Box>
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={700}
                        fontSize="20px"
                        lineHeight="120%"
                        color="text.primary"
                    >
                        {headingText}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0, pt: 1, overflowX: "hidden" }}>
                {typeof alertText === "string" ? (
                    <Typography
                        fontWeight={400}
                        fontSize="14px"
                        lineHeight="150%"
                        color="text.secondary"
                    >
                        {alertText}
                    </Typography>
                ) : (
                    alertText
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", p: 0, pt: 2.5 }}>
                <Button
                    variant="medium"
                    color="primary"
                    onClick={() => {
                        if (onAction) onAction();
                        else onClose();
                    }}
                >
                    {buttonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AlertModal;
